import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import type { RegionInfo, VersionDistribution } from './types';

export function useRegions() {
  return useQuery({
    queryKey: ['fleet', 'regions'],
    queryFn: () => apiFetch<RegionInfo[]>('/fleet/regions'),
    staleTime: 60_000,
  });
}

export function useVersionDistribution() {
  return useQuery({
    queryKey: ['fleet', 'versions'],
    queryFn: () => apiFetch<VersionDistribution[]>('/fleet/versions'),
    staleTime: 60_000,
  });
}

// ─── Fleet upgrade roll (FH-003) ─────────────────────────────────────────

export interface FleetRollItem {
  id: number;
  roll_id: number;
  workspace_id: string;
  tenant_id: string;
  status: 'pending' | 'running' | 'ok' | 'failed' | 'skipped' | 'interrupted';
  skip_reason: string;
  job_id: string;
  log_tail: string;
  started_at: string | null;
  ended_at: string | null;
}

export interface FleetRoll {
  status: 'idle' | 'running' | 'completed' | 'halted' | 'interrupted';
  id?: number;
  reason?: string;
  target_version?: string;
  started_at?: string;
  ended_at?: string | null;
  items?: FleetRollItem[];
}

export function useFleetUpgradeRoll() {
  return useQuery({
    queryKey: ['fleet', 'upgrade-roll'],
    queryFn: () => apiFetch<FleetRoll>('/fleet/upgrade/latest'),
    refetchInterval: query => (query.state.data?.status === 'running' ? 4000 : false),
  });
}

export function useStartFleetUpgrade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      apiFetch<{ ok: boolean; roll_id: number; pending: number; skipped: number }>(
        '/fleet/upgrade',
        { method: 'POST', body: JSON.stringify({ reason }) },
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: ['fleet', 'upgrade-roll'] }),
  });
}
